// Conversion HTML -> texte brut, basique mais suffisante pour les mails Outlook.

import { getAttachmentBase64, MailAttachmentInfo } from "./outlookContext";
// Office.CoercionType.Text fait déjà la conversion la plupart du temps, mais on garde
// un stripper de secours pour les cas où on récupère du HTML brut.

const ENTITIES: Record<string, string> = {
  "&nbsp;": " ",
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&apos;": "'",
  "&#39;": "'",
  "&#x27;": "'",
  "&euro;": "€",
  "&copy;": "©",
  "&reg;": "®",
};

/**
 * Convertit du HTML en texte brut en préservant les retours à la ligne logiques.
 * - <br>, </p>, </div>, </li>, </tr> -> retour ligne
 * - <li> -> "- " en début de ligne
 * - puis suppression de toutes les balises restantes
 * - puis décodage des entités HTML les plus courantes
 * - puis normalisation des blancs (max 2 retours ligne consécutifs).
 */
export function stripHtml(html: string): string {
  if (!html) return "";

  let text = String(html);

  // Suppression des blocs invisibles.
  text = text.replace(/<script[\s\S]*?<\/script>/gi, "");
  text = text.replace(/<style[\s\S]*?<\/style>/gi, "");
  text = text.replace(/<!--[\s\S]*?-->/g, "");

  // Insertion de retours ligne pour les balises de structure.
  text = text.replace(/<\s*br\s*\/?>/gi, "\n");
  text = text.replace(/<\/\s*(p|div|h[1-6]|tr|li|article|section)\s*>/gi, "\n");
  text = text.replace(/<\s*li[^>]*>/gi, "- ");

  // Retrait de toutes les balises restantes.
  text = text.replace(/<[^>]+>/g, "");

  // Décodage des entités nommées connues.
  for (const [k, v] of Object.entries(ENTITIES)) {
    text = text.split(k).join(v);
  }
  // Décodage des entités numériques (&#123; et &#xAB;).
  text = text.replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)));
  text = text.replace(/&#x([0-9a-f]+);/gi, (_, n) => String.fromCharCode(parseInt(n, 16)));

  // Normalisation des blancs : max 2 \\n consécutifs.
  text = text.replace(/\r\n/g, "\n");
  text = text.replace(/[ \t]+\n/g, "\n");
  text = text.replace(/\n{3,}/g, "\n\n");

  return text.trim();
}

/**
 * Format date GLPI : YYYY-MM-DD 00:00:00.
 * GLPI attend ce format avec espace au milieu (pas ISO 8601).
 */
/** Échappement minimal pour injecter du texte dans du HTML GLPI. */
export function escapeHtml(text: string): string {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Normalise une référence cid (src ou contentId Outlook). */
function normalizeContentId(ref: string): string {
  let s = ref.trim();
  if (/^cid:/i.test(s)) s = s.replace(/^cid:/i, "");
  return s.replace(/^<|>$/g, "").trim();
}

/**
 * Remplace les src="cid:…" par des data: URL (images inline Outlook) pour affichage dans GLPI.
 */
export async function embedInlineImagesInHtml(
  html: string,
  attachments: MailAttachmentInfo[]
): Promise<string> {
  if (!html?.trim()) return html || "";

  const inlineAtts = attachments.filter((a) => a.isInline);
  if (inlineAtts.length === 0) return html;

  const dataUrlByKey = new Map<string, string>();
  await Promise.all(
    inlineAtts.map(async (att) => {
      try {
        const { base64, mime } = await getAttachmentBase64(att.id, att);
        const dataUrl = `data:${mime};base64,${base64}`;
        dataUrlByKey.set(att.id, dataUrl);
        if (att.contentId) {
          const cid = normalizeContentId(att.contentId);
          if (cid) {
            dataUrlByKey.set(cid, dataUrl);
            dataUrlByKey.set(`cid:${cid}`, dataUrl);
          }
        }
        if (att.name) {
          const name = normalizeContentId(att.name);
          if (name) dataUrlByKey.set(name, dataUrl);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[GLPI] image inline non chargée", att.name, (e as Error).message);
      }
    })
  );

  if (dataUrlByKey.size === 0) return html;

  if (typeof DOMParser === "undefined") {
    return replaceCidInHtmlString(html, dataUrlByKey);
  }

  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("img").forEach((img) => {
    const src = (img.getAttribute("src") || "").trim();
    if (!src) return;
    const dataUrl = lookupDataUrl(src, dataUrlByKey);
    if (dataUrl) {
      img.setAttribute("src", dataUrl);
    }
  });
  return doc.body.innerHTML;
}

function lookupDataUrl(src: string, map: Map<string, string>): string | undefined {
  const candidates = [
    src,
    normalizeContentId(src),
    src.toLowerCase().startsWith("cid:") ? src : `cid:${normalizeContentId(src)}`,
  ];
  for (const key of candidates) {
    const hit = map.get(key);
    if (hit) return hit;
  }
  return undefined;
}

function replaceCidInHtmlString(html: string, map: Map<string, string>): string {
  return html.replace(
    /(<img\b[^>]*\bsrc\s*=\s*["'])([^"']+)(["'][^>]*>)/gi,
    (full, prefix, src, suffix) => {
      const dataUrl = lookupDataUrl(src.trim(), map);
      return dataUrl ? `${prefix}${dataUrl}${suffix}` : full;
    }
  );
}

/** HTML mail prêt pour GLPI : images cid intégrées puis assainissement. */
export async function prepareMailHtmlForGlpi(
  bodyHtml: string,
  attachments: MailAttachmentInfo[]
): Promise<string> {
  const embedded = await embedInlineImagesInHtml(bodyHtml, attachments);
  return sanitizeHtmlForGlpi(embedded);
}

/**
 * Nettoie le HTML Outlook pour l'envoyer dans le champ `content` GLPI (rich text).
 * Conserve les images data: (inline intégrées). Retire uniquement les cid: non résolus.
 */
export function sanitizeHtmlForGlpi(html: string): string {
  if (!html?.trim()) return "";

  if (typeof DOMParser !== "undefined") {
    const doc = new DOMParser().parseFromString(html, "text/html");
    doc.querySelectorAll("script, style, iframe, object, embed, form, meta, link").forEach((n) => n.remove());

    doc.querySelectorAll("*").forEach((el) => {
      for (const attr of Array.from(el.attributes)) {
        const name = attr.name.toLowerCase();
        if (name.startsWith("on")) {
          el.removeAttribute(attr.name);
        }
        if ((name === "href" || name === "src") && /^\s*javascript:/i.test(attr.value)) {
          el.removeAttribute(attr.name);
        }
      }
    });

    doc.querySelectorAll("img").forEach((img) => {
      const src = img.getAttribute("src") || "";
      if (src.startsWith("cid:")) {
        img.setAttribute("alt", "(image du mail — non chargée)");
        img.removeAttribute("src");
      }
    });

    return doc.body.innerHTML.trim();
  }

  // Secours sans DOMParser
  let text = String(html);
  text = text.replace(/<script[\s\S]*?<\/script>/gi, "");
  text = text.replace(/<style[\s\S]*?<\/style>/gi, "");
  text = text.replace(/\s+on\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "");
  return text.trim();
}

/** En-tête mail en HTML pour le corps du ticket / suivi. */
export function buildMailHtmlHeader(senderName: string, senderEmail: string): string {
  const who = senderName
    ? `${escapeHtml(senderName)} &lt;${escapeHtml(senderEmail)}&gt;`
    : escapeHtml(senderEmail);
  return `<p style="color:#666;font-size:12px;border-bottom:1px solid #ddd;padding-bottom:8px;margin-bottom:12px;"><strong>Mail importé</strong> — De : ${who}</p>`;
}

/**
 * Corps du ticket prêt pour GLPI : HTML assaini ou texte brut.
 */
export function buildTicketBodyContent(options: {
  useRichHtml: boolean;
  bodyHtml: string;
  bodyPlain: string;
  senderName: string;
  senderEmail: string;
}): string {
  const plain = options.bodyPlain.trim();
  if (options.useRichHtml && options.bodyHtml.trim()) {
    return `${buildMailHtmlHeader(options.senderName, options.senderEmail)}${options.bodyHtml}`;
  }
  const header = options.senderEmail
    ? `Mail importé — De : ${options.senderName || options.senderEmail} <${options.senderEmail}>\n\n`
    : "";
  return `${header}${plain}`;
}

export function formatGlpiDate(d: Date, includeTime = false): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  if (!includeTime) {
    return `${yyyy}-${mm}-${dd} 00:00:00`;
  }
  return `${yyyy}-${mm}-${dd} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}
